import { Translation } from "../hooks/useTranslations";

const de: Translation = {
  drivers: {
    edit: "Fahrer bearbeiten",
    new: "Neuer Fahrer",
    singular: "Fahrer",
    plural: "Fahrer",
    delete: "Fahrer löschen",

    questions: {
      delete: "Möchten Sie diesen Fahrer wirklich löschen?",
    },
  },

  subsidiaries: {
    delete: "Niederlassung löschen",
    edit: "Niederlassung bearbeiten",
    new: "Neue Niederlassung",
    singular: "Niederlassung",
    plural: "Niederlassungen",
    versions: "Versionen der Niederlassung",

    questions: {
      delete: "Möchten Sie diese Niederlassung wirklich löschen?",
    },

    errors: {
      notMatchingSubsidiaryFound: "Keine passende Niederlassung gefunden.",
      nameAlreadyExists: "Der Name der Niederlassung wird bereits verwendet.",
      countryISOCodeAlreadyExists:
        "Der angegebene Länder-ISO-Code ist bereits für eine andere Niederlassung definiert.",
      inUseByCars: "Die Niederlassung wird noch von Fahrzeugen verwendet und kann nicht gelöscht werden.",
      inUseByCarOrders: "Die Niederlassung wird noch von einem Fahrzeugkauf verwendet und kann nicht gelöscht werden.",
      missingVatServiceCode: "Fehlender MwSt.-Servicecode der Niederlassung",
      missingVatDeductionCode: "Fehlender MwSt.-Abzugscode der Niederlassung",
      missingCountryIsoCode: "Fehlender Länder-ISO-Code der Niederlassung",
      missingSemiramisOrganizationCode: "Fehlender Semiramis-Organisationscode der Niederlassung",
    },
  },

  manufacturers: {
    delete: "Hersteller löschen",
    edit: "Hersteller bearbeiten",
    new: "Neuer Hersteller",
    singular: "Hersteller",
    plural: "Hersteller",
    versions: "Versionen des Herstellers",

    questions: {
      delete: "Möchten Sie diesen Hersteller wirklich löschen?",
    },

    errors: {
      nameAlreadyUsed: "Ein Hersteller mit demselben Namen existiert bereits.",
      inUseByCars: "Der Hersteller wird noch von Fahrzeugen verwendet und kann nicht gelöscht werden.",
      inUseByCarOrders: "Der Hersteller wird noch von einem Fahrzeugkauf verwendet und kann nicht gelöscht werden.",
    },
  },

  cars: {
    all: "Alle Fahrzeuge",
    delete: "Fahrzeug löschen",
    edit: "Fahrzeug bearbeiten",
    new: "Neues Fahrzeug",
    singular: "Fahrzeug",
    plural: "Fahrzeuge",
    versions: "Fahrzeugversionen",

    questions: {
      delete: "Möchten Sie dieses Fahrzeug wirklich löschen?",
    },

    errors: {
      vinAlreadyUsed: "Ein Fahrzeug mit der angegebenen Fahrgestellnummer existiert bereits.",
      carReadNotAllowed: "Fahrzeuganzeige nicht erlaubt",
    },
  },

  suppliers: {
    delete: "Lieferant löschen",
    edit: "Lieferant bearbeiten",
    new: "Neuer Lieferant",
    singular: "Lieferant",
    plural: "Lieferanten",
    versions: "Lieferantenversionen",

    questions: {
      delete: "Möchten Sie diesen Lieferanten wirklich löschen?",
    },

    errors: {
      nameAlreadyUsed: "Ein Lieferant mit demselben Namen ist bereits definiert.",
      inUseByCarOrders: "Der Lieferant wird noch von Fahrzeugkäufen verwendet und kann nicht gelöscht werden.",
    },
  },

  carOrders: {
    all: "Alle Fahrzeugkäufe",
    delete: "Fahrzeugkauf löschen",
    edit: "Fahrzeugkauf bearbeiten",
    new: "Neuer Fahrzeugkauf",
    singular: "Fahrzeugkauf",
    plural: "Fahrzeugkäufe",
    versions: "Versionen des Fahrzeugkaufs",

    questions: {
      delete: "Möchten Sie diesen Fahrzeugkauf wirklich löschen?",
      deleteLinkedPlanning:
        'Dieser Fahrzeugkauf ist mit einer Fahrzeugplanung verknüpft. Ihr Status wird wieder auf "Zu bestellen" gesetzt.',
    },
    errors: {
      carOrderDateAfterSoldDate: "Das Kaufdatum des Fahrzeugs darf nicht nach dem Verkaufsdatum liegen.",
    },
  },

  carPlates: {
    delete: "Kennzeichen löschen",
    edit: "Kennzeichen bearbeiten",
    new: "Neues Kennzeichen",
    singular: "Kennzeichen",
    plural: "Kennzeichen",
    versions: "Kennzeichenversionen",

    questions: {
      delete: "Möchten Sie dieses Kennzeichen wirklich löschen?",
    },

    errors: {
      plateOverlapPeriodSub:
        "Das Kennzeichen wird im gleichen Zeitraum bereits von einem anderen Fahrzeug derselben Niederlassung verwendet.",
      plateOverlapPeriodCar: "Der angegebene Zeitraum überschneidet sich mit einem anderen Kennzeichen.",
      carPlateOutOfSubsidiaryPeriod: "Der Kennzeichenzeitraum liegt nicht im Zeitraum der Niederlassung.",
    },
  },
  carDrivers: {
    delete: "Fahrzeugfahrer löschen",
    edit: "Fahrzeugfahrer bearbeiten",
    new: "Neuer Fahrzeugfahrer",
    singular: "Fahrzeugfahrer",
    plural: "Fahrzeugfahrer",
    versions: "Versionen des Fahrzeugfahrers",
    sendMileageEmail: "Kilometerstand-E-Mail an den neuen Fahrer senden",
    mileageEmailFirstAssignment: "Die E-Mail kann nicht gesendet werden: Dies ist der erste Fahrer dieses Fahrzeugs.",
    mileageEmailNotificationsDisabled: (flag: string) =>
      `Die E-Mail kann nicht gesendet werden: Beim Fahrer ist die Option "${flag}" aktiviert.`,
    mileageEmailNoEmail: "Die E-Mail kann nicht gesendet werden: Dieser Fahrer hat keine E-Mail-Adresse.",

    questions: {
      delete: "Möchten Sie diesen Fahrzeugfahrer wirklich löschen?",
    },

    errors: {
      overlapsPeriod: "Der angegebene Zeitraum überschneidet sich mit einem anderen Fahrzeugfahrer.",
      overlapsPeriodPool: "Der angegebene Zeitraum überschneidet sich mit einem anderen im Car Pool.",
      carDriverOutOfSubsidiaryPeriod: "Der Fahrerzeitraum liegt nicht im Zeitraum der Niederlassung.",
      delegationLanguageNotFound: "Die ausgewählte Sprache ist für Delegationen nicht verfügbar.",
      delegationSubsidiaryNotConfigured:
        "Die Delegationsdaten sind für den Semiramis-Organisationscode der Niederlassung nicht konfiguriert.",
    },
  },

  carLeasings: {
    delete: "Fahrzeugleasing löschen",
    edit: "Fahrzeugleasing bearbeiten",
    conclude: "Fahrzeugleasing abschließen",
    new: "Neues Fahrzeugleasing",
    singular: "Fahrzeugleasing",
    plural: "Fahrzeugleasings",
    versions: "Versionen des Fahrzeugleasings",

    questions: {
      delete: "Möchten Sie dieses Fahrzeugleasing wirklich löschen?",
      conclude: "Möchten Sie dieses Fahrzeugleasing wirklich abschließen?",
    },

    errors: {
      alreadyHasLeasing: "Für das angegebene Fahrzeug ist bereits ein Leasing definiert.",
      expirationDateCantMatchContractDate: "Das Ablaufdatum darf nicht dem Vertragsdatum entsprechen.",
      expirationDateCantBeBeforeContractDate: "Das Ablaufdatum darf nicht vor dem Vertragsdatum liegen.",
    },
  },

  mileages: {
    delete: "Kilometerstand löschen",
    edit: "Kilometerstand bearbeiten",
    new: "Neuer Kilometerstand",
    singular: "Kilometerstand",
    plural: "Kilometerstände",
    versions: "Versionen des Kilometerstands",
    sendReminder: "Kilometerstand-Erinnerung senden",
    sendReminders: "Erinnerungen senden",

    questions: {
      delete: "Möchten Sie diesen Kilometerstand wirklich löschen?",
      sendReminder:
        "Möchten Sie dem aktuellen Fahrer eine E-Mail senden, die ihn an die Eingabe des aktuellen Kilometerstands dieses Fahrzeugs erinnert?",
    },

    reminderDialog: {
      recipient: "Empfänger",
      lastReminder: "Letzte an den Fahrer gesendete Kilometerstand-Erinnerung",
      noReminderSent: "Keine Erinnerung gesendet",
      automaticReminderRule:
        "Fleet Manager sendet automatisch eine Kilometerstand-Erinnerung an die Fahrer von Fahrzeugen ohne Kilometerstand in den letzten 3 Monaten, überspringt aber die Fahrer, die in den letzten 3 Tagen bereits eine erhalten haben. Wenn Sie das Datum der letzten Erinnerung aktualisieren, gilt diese E-Mail als letzte gesendete Erinnerung und der Fahrer erhält in den nächsten 3 Tagen keine automatische Erinnerung, auch wenn in diesem Zeitraum die 3 Monate ablaufen.",
      updateLastReminder: "Datum der letzten Erinnerung aktualisieren",
      driverNotificationsDisabled: (flag) =>
        `Die E-Mail kann nicht gesendet werden: Beim Fahrer ist das Flag "${flag}" aktiviert.`,
      driverNoEmail: "Die E-Mail kann nicht gesendet werden: Der Fahrer hat keine E-Mail-Adresse.",
    },

    remindersDialog: {
      question:
        "Möchten Sie den aktuellen Fahrern der aktiven Fahrzeuge eine E-Mail senden, die sie an die Eingabe des aktuellen Kilometerstands ihres Fahrzeugs erinnert?",
      recipients: "Empfänger",
      allDrivers: "Alle Fahrer",
      onlyOverdue: "Nur überfälliger Kilometerstand (über 3 Monate)",
      allSubsidiaries: "Alle Niederlassungen",
      summary: (drivers, emails) =>
        `Zu benachrichtigende Fahrer: ${drivers} (zu sendende E-Mails: ${emails}, eine pro Fahrzeug).`,
      excluded: (excluded) =>
        `Ausgeschlossene Fahrer, weil die Benachrichtigungen deaktiviert sind oder keine E-Mail-Adresse vorhanden ist: ${excluded}.`,
      noRecipients: "Keine Fahrer zu benachrichtigen.",
      onlyOverdueInfo: (option) =>
        `Mit "${option}" werden nur die Fahrer von Fahrzeugen benachrichtigt, deren letzter Kilometerstand älter als 3 Monate ist oder die noch nie einen Kilometerstand hatten.`,
      updateLastReminderInfo: (switchLabel) =>
        `Fleet Manager sendet außerdem eine automatische Erinnerung an die Fahrer mit überfälligem Kilometerstand, überspringt aber diejenigen, die in den letzten 3 Tagen bereits eine erhalten haben. Wenn Sie "${switchLabel}" aktivieren, wird das Datum der letzten Erinnerung der benachrichtigten Fahrer auf heute gesetzt: In den nächsten 3 Tagen erhalten sie keine automatische Erinnerung, auch wenn in diesem Zeitraum die 3 Monate ablaufen.`,
      backgroundInfo:
        "Die Erinnerungen werden im Hintergrund gesendet: Sie können dieses Fenster schließen und weiterarbeiten. Nach Abschluss werden Sie benachrichtigt.",
      alreadySending:
        "Es werden bereits Kilometerstand-Erinnerungen gesendet: Sie können einen neuen Versand starten, sobald dieser abgeschlossen ist.",
      sent: "Kilometerstand-Erinnerungen gesendet.",
    },

    notNotifiedDialog: {
      title: "Nicht gesendete Kilometerstand-Erinnerungen",
      text: "Die Kilometerstand-Erinnerung konnte an folgende Fahrer nicht gesendet werden:",
    },

    errors: {
      mileageWithThisDateAlreadyExists: "Für das ausgewählte Datum wurde derselbe Kilometerstand bereits erfasst.",
      distanceLowerPrevRecord: "Der eingegebene Kilometerstand ist kleiner als dem vorherigen Eintrag.",
      distanceHigherNextRecord: "Der Kilometerstand ist größer als dem nächsten Eintrag.",
      mileageOutOfSubsidiaryPeriod: "Das Datum des Kilometerstands liegt nicht im Zeitraum der Niederlassung.",
      mileageOutOfCarDriverPeriod:
        "Das Datum der Fahrt liegt außerhalb des Zeitraums, in dem dir das Fahrzeug zugewiesen ist.",
      mileageMustBeBetween: (prevDate, prevDistance, nextDate, nextDistance) =>
        `Für das ausgewählte Datum muss die Distanz zwischen ${prevDistance} (am ${prevDate}) und ${nextDistance} (am ${nextDate}) liegen.`,
      mileageMustBeGreater: (distance, date) =>
        `Für das ausgewählte Datum muss die Distanz größer sein als ${distance} (am ${date}).`,
      mileageMustBeLower: (distance, date) =>
        `Für das ausgewählte Datum muss die Distanz kleiner sein als ${distance} (am ${date}).`,
      reminderMissingCurrentDriver: "Die E-Mail kann nicht gesendet werden: Das Fahrzeug hat keinen aktuellen Fahrer.",
      reminderDriverMissingEmail: "Die E-Mail kann nicht gesendet werden: Der Fahrer hat keine E-Mail-Adresse.",
      reminderDriverNotificationsDisabled:
        "Die E-Mail kann nicht gesendet werden: Der Fahrer hat die Benachrichtigungen deaktiviert.",
      reminderSubsidiaryNotAllowed: "Sie haben keinen Zugriff auf die ausgewählte Niederlassung.",
    },
  },

  defaultTasks: {
    new: "Neue Standardaufgabe",
    edit: "Standardaufgabe bearbeiten",
    delete: "Standardaufgabe löschen",
    singular: "Standardaufgabe",
    plural: "Standardaufgaben",

    questions: {
      delete: "Möchten Sie diese Standardaufgabe wirklich löschen?",
    },
  },

  customTasks: {
    set: "Eigene Aufgaben festlegen",
    create: "Eigene Aufgaben erstellen",
    edit: "Eigene Aufgaben bearbeiten",
    singular: "Eigene Aufgabe",
    plural: "Eigene Aufgaben",

    questions: {
      customize: "Die Niederlassung verwendet derzeit die Standardaufgaben. Möchten Sie diese anpassen?",
    },

    errors: {
      taskAlreadySelected: "Die Aufgabe wurde bereits ausgewählt.",
    },
  },

  tasks: {
    delete: "Aufgabe löschen",
    rename: "Aufgabe umbenennen",
    edit: "Aufgabe bearbeiten",
    new: "Neue Aufgabe",
    singular: "Aufgabe",
    plural: "Aufgaben",
    versions: "Aufgabenversionen",

    questions: {
      delete: "Möchten Sie diese Aufgabe wirklich löschen?",
    },

    errors: {
      taskNameAlreadyExists: "Eine Standardaufgabe mit demselben Namen ist bereits definiert.",
      inUseByCustomTasks:
        "Die Standardaufgabe wird noch von einer eigenen Aufgabe verwendet und kann nicht gelöscht werden.",
    },
  },

  tasksCategories: {
    delete: "Aufgabenkategorie löschen",
    plural: "Aufgabenkategorien",

    questions: {
      deleteSubsidiaryCustomTasks:
        "Möchten Sie diese benutzerdefinierte Kategorie wirklich löschen und die Standardaufgaben zuordnen?",
    },
  },

  carTasks: {
    delete: "Task löschen",
    edit: "Task bearbeiten",
    new: "Neuer Task",
    singular: "Task",
    plural: "Tasks",

    questions: {
      delete: "Möchten Sie diesen Task wirklich löschen?",
      deleteWithWorkflow:
        "Diese Aufgabe ist mit einem Workflow verknüpft. Wenn Sie fortfahren, werden sowohl die Aufgabe als auch der zugehörige Workflow gelöscht. Möchten Sie fortfahren?",
    },

    errors: {
      inUseByWorkflows: "Der Task wird noch von einem Workflow verwendet und kann nicht gelöscht werden.",
    },
  },

  leasingCompanies: {
    delete: "Leasinggesellschaft löschen",
    edit: "Leasinggesellschaft bearbeiten",
    new: "Neue Leasinggesellschaft",
    singular: "Leasinggesellschaft",
    plural: "Leasinggesellschaften",
    versions: "Versionen der Leasinggesellschaft",

    questions: {
      delete: "Möchten Sie diese Leasinggesellschaft wirklich löschen?",
    },

    errors: {
      nameAlreadyExists: "Eine Leasinggesellschaft mit demselben Namen ist bereits definiert.",
      inUseByCarLeasings:
        "Die Leasinggesellschaft wird noch von einem Fahrzeugleasing verwendet und kann nicht gelöscht werden.",
    },
  },

  plateNumbers: {
    singular: "Kennzeichen",
  },

  highwayPasses: {
    delete: "Mautgerät löschen",
    edit: "Mautgerät bearbeiten",
    new: "Neues Mautgerät",
    singular: "Mautgerät",
    plural: "Mautgeräte",
    versions: "Mautgerätversionen",

    questions: {
      delete: "Möchten Sie dieses Mautgerät wirklich löschen?",
    },

    errors: {
      highwayPassOverlaps:
        "Ein Mautgerät mit derselben Nummer und für den angegebenen Zeitraum wird bereits von einem anderen Fahrzeug verwendet.",
      highwayPassOutOfSubsidiaryPeriod: "Der Zeitraum des Mautgeräts liegt nicht im Zeitraum der Niederlassung.",
    },
  },
  costTypeCategories: {
    delete: "Kostenartkategorie löschen",
    edit: "Kostenartkategorie bearbeiten",
    new: "Neue Kostenartkategorie",
    singular: "Kostenartkategorie",
    plural: "Kostenartkategorien",
    versions: "Versionen der Kostenartkategorie",

    questions: {
      delete: "Möchten Sie diese Kostenartkategorie wirklich löschen?",
    },

    errors: {
      isUsedByCostType:
        "Die Kostenartkategorie wird noch von einer Kostenart verwendet und kann nicht gelöscht werden.",
      nameAlreadyUsed: "Eine Kostenartkategorie mit demselben Namen ist bereits definiert.",
    },
  },

  costTypes: {
    delete: "Kostenart löschen",
    edit: "Kostenart bearbeiten",
    new: "Neue Kostenart",
    singular: "Kostenart",
    plural: "Kostenarten",
    versions: "Versionen der Kostenart",

    questions: {
      delete: "Möchten Sie diese Kostenart wirklich löschen?",
    },

    errors: {
      isUsedByCosts: "Die Kostenart wird noch von einem Kostenposten verwendet und kann nicht gelöscht werden.",
      nameAlreadyUsed: "Eine Kostenart mit diesem Namen ist bereits definiert.",
    },
  },

  costs: {
    delete: "Kosten löschen",
    edit: "Kosten bearbeiten",
    new: "Neue Kosten",
    singular: "Kosten",
    plural: "Kosten",
    versions: "Kostenversionen",

    questions: {
      delete: "Möchten Sie diese Kosten wirklich löschen?",
    },

    errors: {
      costOutOfSubsidiaryPeriod: "Das Datum der Kosten liegt nicht im Zeitraum der Niederlassung.",
      missingCurrency: (currency: string) =>
        `Kosten können nicht in EUR umgerechnet werden: Währung ${currency} nicht gefunden.`,
    },
  },

  maintenanceTypes: {
    delete: "Wartungstyp löschen",
    edit: "Wartungstyp bearbeiten",
    new: "Neuer Wartungstyp",
    singular: "Wartungstyp",
    plural: "Wartungstypen",

    questions: {
      delete: "Möchten Sie diesen Wartungstyp wirklich löschen?",
    },

    errors: {
      isUsedByMaintenance:
        "Der Wartungstyp wird noch von einem Wartungsdatensatz verwendet und kann nicht gelöscht werden.",
      nameAlreadyUsed: "Ein Wartungstyp mit demselben Namen ist bereits definiert.",
    },
  },

  maintenance: {
    delete: "Wartung löschen",
    edit: "Wartung bearbeiten",
    new: "Neue Wartung",
    singular: "Wartung",
    plural: "Wartungen",

    questions: {
      delete: "Möchten Sie diesen Wartungsdatensatz wirklich löschen?",
    },
  },

  costsImport: {
    delete: "Kostenimport löschen",
    changeCarAssignment: "Zugeordnetes Fahrzeug ändern",
    changeCarAssignmentDisclaimer: (plate: string, date: string) =>
      `Diese Kosten wurden dem Kennzeichen ${plate} am ${date} in Rechnung gestellt. Bevor Sie manuelle Änderungen vornehmen, überprüfen Sie bitte die aktuellen Daten in Fleet Management, um sicherzustellen, dass diese Korrektur wirklich erforderlich ist.`,
    noDriverCoversDate: "Keine der oben aufgeführten Zuweisungen deckt das Datum dieses Kosteneintrags ab.",
    missingAssignmentCostCenter: (name: string) =>
      `${name} deckt das Datum dieser Kosten ab, hat aber keine Kostenstelle definiert.`,
    carForcedManually: (date: string) => `Das Fahrzeug wurde am ${date} manuell zugewiesen.`,
    assignmentForcedManually: (date: string) => `Die Fahrerzuweisung wurde am ${date} manuell erzwungen.`,
    carForcedFromTo: (fromPlate: string, toPlate: string, name: string, date: string) =>
      `Diese Fahrzeugzuweisung wurde von ${fromPlate} auf ${toPlate} durch ${name} am ${date} geändert.`,
    removeForcedCar: "Erzwingung entfernen",
    costWillBeAssignedTo: (name: string) => `Diese Kosten werden ${name} zugewiesen.`,
    forceThisDriver: "Diesen Fahrer erzwingen",
    removeAssignment: "Zuweisung entfernen",
    forcedAssignmentDoesNotCoverDate: "Diese Zuweisung deckt das Datum dieses Kosteneintrags nicht ab.",
    excludedFromExport: (plate: string) =>
      `Für ${plate} wird diese Zeile nicht in die Kosten exportiert: Es werden nur Pkw-, Pkw-superior- und Lkw-Fahrzeuge exportiert.`,

    questions: {
      deleteImport: "Möchten Sie diesen Kostenimport wirklich löschen?",
      importInCosts: "Möchten Sie diese Zeilen wirklich in die Kosten importieren?",
      forceDriver: (name: string) =>
        `Möchten Sie diese Kosten wirklich ${name} zuweisen? Dies überschreibt die automatische, datumsbasierte Zuweisung.`,
    },

    dkv: {
      noCarFound: (plate: string, date: string) =>
        `Für das Kennzeichen ${plate} deckt kein Fahrzeug die Kosten am ${date} ab`,
      noDriverFound: (plate: string, date: string) =>
        `Für das Fahrzeug ${plate} deckt keine Zuweisung die Kosten am ${date} ab`,
      noSubsidiaryVatCodeFound: (productCode: string) => `Für ${productCode} kein MwSt.-Code gefunden`,
      noCostTypeFound: (productCode: string, category?: string) =>
        `Für ${productCode}${category ? `/${category}` : ""} kein Kostentyp gefunden`,
      noCostCenterFound: (name: string, date: string) =>
        `Für ${name} deckt keine Kostenstelle die Kosten am ${date} ab`,
      noUserCostCenterFound: (plate: string, date: string) =>
        `Für das Fahrzeug ${plate} deckt keine Benutzerkostenstelle die Kosten am ${date} ab`,

      errors: {
        columnTargaVeicolo: "Spalte 'Targa veicolo' nicht gefunden",
        columnDataFattura: "Spalte 'Data della fattura' nicht gefunden",
        columnOraTransazione: "Spalte 'Ora transazione' nicht gefunden",
        columnValoreBaseNetto: "Spalte 'Valore base netto' nicht gefunden",
        columnValoreBaseLordo: "Spalte 'Valore base lordo' nicht gefunden",
        columnCodiceMerce: "Spalte 'Codice merce' nicht gefunden",
        columnGruppoProdotti: "Spalte 'Gruppo di prodotti' nicht gefunden",
        columnTipoMerce: "Spalte 'Tipo di merce' nicht gefunden",
        columnPaesePedaggio: "Spalte 'Paese di pedaggio' nicht gefunden",
        columnIva: "Spalte 'IVA' nicht gefunden",
        columnScontoNetto: "Spalte 'Sconto netto' nicht gefunden",
        columnScontoLordo: "Spalte 'Sconto lordo' nicht gefunden",
        columnServiceFeeNetto: "Spalte 'Service fee netto' nicht gefunden",
        columnQuantita: "Spalte 'Quantità' nicht gefunden",
        columnUnita: "Spalte 'Unità' nicht gefunden",
      },
    },

    dkvTolls: {
      errors: {
        columnDataFattura: "Spalte 'Data della fattura' nicht gefunden",
        columnTargaVeicolo: "Spalte 'Targa veicolo' nicht gefunden",
        columnOraUscita: "Spalte 'Ora uscita' nicht gefunden",
        columnCodiceMerce: "Spalte 'Codice merce' nicht gefunden",
        columnValutaServizio: "Spalte 'Valuta del servizio' nicht gefunden",
        columnIva: "Spalte 'IVA' nicht gefunden",
        columnValoreBaseNetto: "Spalte 'Valore base netto' nicht gefunden",
        columnPaesePedaggio: "Spalte 'Paese di pedaggio' nicht gefunden",
      },
    },
  },

  dueDateTypes: {
    delete: "Fälligkeitstyp löschen",
    edit: "Fälligkeitstyp bearbeiten",
    new: "Neuer Fälligkeitstyp",
    singular: "Fälligkeitstyp",
    plural: "Fälligkeitstypen",
    versions: "Versionen des Fälligkeitstyps",

    questions: {
      delete: "Möchten Sie diesen Fälligkeitstyp wirklich löschen?",
    },

    errors: {
      isUsedByDueDates: "Der Fälligkeitstyp wird noch von einer Fälligkeit verwendet und kann nicht gelöscht werden.",
      nameAlreadyUsed: "Ein Fälligkeitstyp mit demselben Namen ist bereits definiert.",
    },
  },

  dueDates: {
    all: "Alle Fälligkeiten",
    delete: "Fälligkeit löschen",
    edit: "Fälligkeit bearbeiten",
    new: "Neue Fälligkeit",
    singular: "Fälligkeit",
    plural: "Fälligkeiten",
    versions: "Fälligkeitversionen",
    renewPlural: "Fälligkeiten erneuern",
    renewSingular: "Fälligkeit erneuern",
    renewSelected: "Ausgewählte Fälligkeiten erneuern",
    setSettledSelected: "Ausgewählte Fälligkeiten als erledigt markieren",

    questions: {
      delete: "Möchten Sie diese Fälligkeit wirklich löschen?",
      setSettled: "Möchten Sie die ausgewählten Fälligkeiten wirklich als erledigt markieren?",
    },

    errors: {
      dueDateOutOfSubsidiaryPeriod: "Das Fälligkeitsdatum liegt nicht im Zeitraum der Niederlassung.",
    },
  },

  documents: {
    add: "Dokument hinzufügen",
    all: "Alle Dokumente",
    alreadyPresent: "Dokument bereits vorhanden.",
    plural: "Dokumente",
    delete: "Dokument löschen",
    search: "Dokumente suchen",

    questions: {
      delete: "Möchten Sie dieses Dokument wirklich löschen?",
      overwrite: "Möchten Sie das bereits vorhandene Dokument überschreiben?",
    },

    errors: {
      notFound: "Das Dokument wurde nicht gefunden",
    },
  },

  users: {
    all: "Alle Benutzer",
    delete: "Benutzer löschen",
    edit: "Benutzer bearbeiten",
    new: "Neuer Benutzer",
    singular: "Benutzer",
    plural: "Benutzer",

    questions: {
      delete: "Möchten Sie diesen Benutzer wirklich löschen?",
    },

    errors: {
      noMatchingUserFound: "Kein passender Benutzer gefunden.",
      rothoblaasUserWithEmailNotFound:
        "Ein Rothoblaas-Benutzer mit der angegebenen E-Mail-Adresse wurde nicht gefunden.",
      usersWithEmailAlreadyExists: "Ein Benutzer mit der angegebenen E-Mail-Adresse ist bereits definiert.",
      inUseByCarTasks: "Der Benutzer wird noch von einem Task verwendet und kann nicht gelöscht werden.",
      inUseByCarOrders: "Der Benutzer wird noch von einem Fahrzeugkauf verwendet und kann nicht gelöscht werden.",
      inUseByCarDrivers: "Der Benutzer wird noch als Fahrer verwendet und kann nicht gelöscht werden.",
      usersWithEmailNotFound: "Ein Benutzer mit der angegebenen E-Mail-Adresse wurde nicht gefunden.",
      invalidEmailDomain: "Die E-Mail-Domain ist für Rothoblaas-Benutzer ungültig.",
      userSubsidiaryAlreadyExists: "Der Benutzer ist der angegebenen Niederlassung bereits zugewiesen.",
      userInUseAsManager: "Der Benutzer wird noch als Manager verwendet und kann nicht gelöscht werden.",
    },
  },

  claims: {
    all: "Alle Schadensfälle",
    delete: "Schadensfall löschen",
    edit: "Schadensfall bearbeiten",
    new: "Neuer Schadensfall",
    singular: "Schadensfall",
    plural: "Schadensfälle",
    incidentDate: "Schadensdatum",
    openingDate: "Eröffnungsdatum",
    closingDate: "Abschlussdatum",
    damageValue: "Schadenswert",
    settledAmount: "Regulierter Betrag",

    questions: {
      delete: "Möchten Sie diesen Schadensfall wirklich löschen?",
      switchDriver: (selectedDriver: string, currentDriver: string) =>
        `Der ausgewählte Fahrer ist ${selectedDriver}, aber der aktuell zugewiesene Fahrer ist ${currentDriver}.<br /> Möchten Sie diesen Schadensfall ${currentDriver} zuweisen?`,
      switchSubsidiary: (selectedSubsidiary: string, currentSubsidiary: string) =>
        `Die ausgewählte Niederlassung ist ${selectedSubsidiary}, aber die aktuell zugewiesene ist ${currentSubsidiary}.<br /> Möchten Sie diesen Schadensfall ${currentSubsidiary} zuweisen?`,
    },
  },

  fines: {
    delete: "Bußgeld löschen",
    edit: "Bußgeld bearbeiten",
    new: "Neues Bußgeld",
    singular: "Bußgeld",
    plural: "Bußgelder",
    verbalNumber: "Bußgeldnummer",
    violationDate: "Verstoßdatum",
    communicationDate: "Mitteilungsdatum",
    firstNotificationDate: "Datum der ersten Benachrichtigung",
    amount: "Betrag",
    points: "Punkte",
    accountant: "Buchhalter",
    hrReferent: "HR-Ansprechpartner",
    notifyDriver: "Fahrer benachrichtigen",
    archive: "Archivieren",
    notifications: "Benachrichtigungen",
    sentBy: "Gesendet von",
    sentAt: "Gesendet am",
    notifyDialog: {
      recipient: "Empfänger",
      attachments: "Anhänge",
      noAttachments: "Wählen Sie mindestens ein Dokument zum Versenden aus.",
      addDocuments: "Dokumente hinzufügen",
      dropDocumentsHint: "Sie können die Dateien auch auf die Tabelle ziehen",
      attachmentsHint: "Hier hinzugefügte Dokumente werden auch in den Dokumenten des Bußgelds gespeichert.",
      alreadyNotified: (name: string, date: string) => `Bereits von ${name} am ${date} benachrichtigt.`,
      driverNotificationsDisabled: (flag: string) =>
        `Die E-Mail kann nicht gesendet werden: Beim Fahrer ist das Flag "${flag}" aktiviert.`,
      driverNoEmail: "Die E-Mail kann nicht gesendet werden: Der Fahrer hat keine E-Mail-Adresse.",
      userNotificationsDisabled: (flag: string) =>
        `Bei diesem Benutzer ist das Flag "${flag}" aktiviert, er kann keine Kopie erhalten.`,
      userNoEmail: "Dieser Benutzer hat keine E-Mail-Adresse und kann keine Kopie erhalten.",
    },
    errors: {
      notifyArchived: "Ein archiviertes Bußgeld kann nicht gemeldet werden.",
      alreadyArchived: "Das Bußgeld ist bereits archiviert.",
      driverMissingEmail: "Die E-Mail kann nicht gesendet werden: Der Fahrer hat keine E-Mail-Adresse.",
      driverNotificationsDisabled:
        "Die E-Mail kann nicht gesendet werden: Beim Fahrer sind Benachrichtigungen deaktiviert.",
    },
    statuses: {
      new: "Neu",
      notified: "Benachrichtigt",
      archived: "Archiviert",
    },
    questions: {
      delete: "Möchten Sie dieses Bußgeld wirklich löschen?",
      archive: "Möchten Sie dieses Bußgeld wirklich archivieren?",
    },
  },

  claimTypes: {
    all: "Alle Schadensarten",
    delete: "Schadensart löschen",
    edit: "Schadensart bearbeiten",
    new: "Neue Schadensart",
    singular: "Schadensart",
    plural: "Schadensarten",

    questions: {
      delete: "Möchten Sie diese Schadensart wirklich löschen?",
    },

    errors: {
      nameAlreadyUsed: "Eine Schadensart mit diesem Namen existiert bereits.",
      inUseByClaims: "Der Schadentyp wird noch in Schadensfällen verwendet und kann nicht gelöscht werden.",
    },
  },

  clients: {
    all: "Alle Clients",
    plural: "Clients",
    createNew: "Neuen Client erstellen",
    delete: "Client löschen",
    search: "Clients suchen",

    questions: {
      delete: "Möchten Sie diesen Client wirklich löschen?",
    },

    errors: {
      notFound: "Kunde nicht gefunden.",
      missingName: "Client-Name fehlt.",
      insertNotAllowed: "Sie sind nicht berechtigt, einen neuen Client anzulegen.",
      readNotAllowed: "Sie sind nicht berechtigt, diesen Client einzusehen.",
      updateNotAllowed: "Sie sind nicht berechtigt, diesen Client zu aktualisieren.",
      deleteNotAllowed: "Sie sind nicht berechtigt, diesen Client zu löschen.",
    },
  },

  subsidiaryVatCodes: {
    delete: "MwSt.-Code löschen",
    edit: "MwSt.-Code bearbeiten",
    new: "Neuer MwSt.-Code",
    singular: "MwSt.-Code",
    plural: "MwSt.-Codes",
    versions: "Versionen des MwSt.-Codes",

    questions: {
      delete: "Möchten Sie diesen MwSt.-Code der Niederlassung wirklich löschen?",
    },
  },

  roles: {
    delete: "Rolle löschen",
    edit: "Rolle bearbeiten",
    new: "Neue Rolle",
    singular: "Rolle",
    plural: "Rollen",

    questions: {
      delete: "Möchten Sie diese Rolle wirklich löschen?",
    },
  },

  permissions: {
    plural: "Berechtigungen",
  },

  secrets: {
    plural: "Geheimnisse",

    errors: {
      missingName: "Name des Geheimnisses fehlt.",
      missingValue: "Wert des Geheimnisses fehlt.",
    },
  },

  carPoolCostCenters: {
    delete: "Car Pool Kostenstelle löschen",
    edit: "Car Pool Kostenstelle bearbeiten",
    new: "Neue Car Pool Kostenstelle",
    singular: "Car Pool Kostenstelle",
    plural: "Car Pool Kostenstellen",

    questions: {
      delete: "Möchten Sie diese Car Pool Kostenstelle wirklich löschen?",
    },

    errors: {
      carPoolOverlapPeriod: "Im Car Pool ist bereits eine Kostenstelle für diesen Zeitraum vorhanden.",
    },
  },

  otherCarAssignments: {
    delete: "Weitere Fahrzeugzuweisung löschen",
    edit: "Weitere Fahrzeugzuweisung bearbeiten",
    new: "Neue weitere Fahrzeugzuweisung",
    singular: "Weitere Fahrzeugzuweisung",
    plural: "Weitere Fahrzeugzuweisungen",

    questions: {
      delete: "Möchten Sie diese weitere Fahrzeugzuweisung wirklich löschen?",
    },

    errors: {
      carPoolOverlapPeriod: "Das Fahrzeug befindet sich im angegebenen Zeitraum bereits im Car Pool.",
      carPoolOverlapPeriodWithDrivers: "Das Fahrzeug hat im angegebenen Zeitraum bereits einen Fahrer.",
      assignmentOutOfSubsidiaryPeriod: "Der Zuweisungszeitraum liegt nicht im Zeitraum der Niederlassung.",
    },
  },

  carPlannings: {
    delete: "Fahrzeugplanung löschen",
    edit: "Fahrzeugplanung bearbeiten",
    new: "Neue Fahrzeugplanung",
    singular: "Fahrzeugplanung",
    plural: "Fahrzeugplanungen",
    versions: "Versionen der Fahrzeugplanung",

    questions: {
      delete: "Möchten Sie diese Fahrzeugplanung wirklich löschen?",
    },

    errors: {
      missingSemiramisCode:
        "Tochtergesellschaft ohne Semiramis-Organisationscode. Bitte legen Sie den Semiramis-Code in der ausgewählten Tochtergesellschaft fest",
    },
  },

  carSubsidiaries: {
    delete: "Fahrzeugniederlassung löschen",
    edit: "Fahrzeugniederlassung bearbeiten",
    new: "Neue Fahrzeugniederlassung",
    singular: "Fahrzeugniederlassung",
    plural: "Fahrzeugniederlassungen",
    versions: "Versionen der Fahrzeugniederlassung",

    questions: {
      delete: "Möchten Sie diese Fahrzeugniederlassung wirklich löschen?",
    },

    errors: {
      overlapPeriodCarSubsidiary: "Der angegebene Zeitraum überschneidet sich mit einer anderen Niederlassung.",
      overlapPeriodCarPlate:
        "Es gibt Fahrzeugkennzeichen, deren Zeiträume sich mit anderen Niederlassungen überschneiden.",
      overlapPeriodCarDriver: "Es gibt Fahrzeugfahrer, deren Zeiträume sich mit anderen Niederlassungen überschneiden.",
    },
  },

  rentalCars: {
    delete: "Mietwagen löschen",
    edit: "Mietwagen bearbeiten",
    new: "Neuer Mietwagen",
    singular: "Mietwagen",
    plural: "Mietwagen",

    questions: {
      delete: "Möchten Sie diesen Mietwagen wirklich löschen?",
    },

    errors: {
      overlapPeriodCar: "Der angegebene Zeitraum überschneidet sich mit einem anderen Mietwagen.",
    },
  },

  userSubsidiaries: {
    delete: "Benutzer-Niederlassung löschen",
    edit: "Benutzer-Niederlassung bearbeiten",
    new: "Neue Benutzer-Niederlassung",
    singular: "Benutzer-Niederlassung",
    plural: "Benutzer-Niederlassungen",
    versions: "Versionen der Benutzer-Niederlassung",

    questions: {
      delete: "Möchten Sie diese Benutzer-Niederlassung wirklich löschen?",
    },
  },

  userRoles: {
    delete: "Benutzerrolle löschen",
    edit: "Benutzerrolle bearbeiten",
    new: "Neue Benutzerrolle",
    singular: "Benutzerrolle",
    plural: "Benutzerrollen",
    versions: "Versionen der Benutzerrolle",

    questions: {
      delete: "Möchten Sie diese Benutzerrolle wirklich löschen?",
    },

    errors: {
      alreadyExists: "Die Benutzerrolle existiert bereits",
    },
  },

  common: {
    acceptImages: "Bilder akzeptieren",
    acceptImagesDialog: "Die hochgeladenen Bilder werden akzeptiert, und der Fahrer wird per E-Mail benachrichtigt.",
    accessNotAllowed: "Zugriff nicht erlaubt",
    accessNotAllowedError: "Sie haben keine Berechtigung, diese Seite anzusehen.",
    active: "Aktiv",
    action: "Aktion",
    accounting: "Buchhaltung",
    add: "Hinzufügen",
    addCustomTask: "Benutzerdefinierten Task hinzufügen",
    addDefaultTask: "Standard-Task hinzufügen",
    addCustomCarTask: "Benutzerdefinierten Fahrzeug-Task hinzufügen",
    addDefaultCarTask: "Standard-Fahrzeug-Task hinzufügen",
    addDueDate: "Fälligkeit hinzufügen",
    addNew: "Neu hinzufügen",
    addNewAssignment: "Neue Zuweisung hinzufügen",
    addMileage: "Kilometerstand hinzufügen",
    address: "Adresse",
    addTask: "Aufgabe hinzufügen",
    addToCarPool: "Zum Car Pool hinzufügen",
    addToHistory: "Zur Historie hinzufügen",
    admin: "Admin",
    administration: "Administration",
    agreement: "Vereinbarung",
    all: "Alle",
    allowCarsToHaveMultiplePlates: "Fahrzeugen mehrere Kennzeichen erlauben",
    allowMultiplePlates: "Mehrere Kennzeichen erlauben",
    alreadyInCarPool: "Fahrzeug bereits im Car Pool",
    alreadyPresent: "Bereits vorhanden",
    alreadyUpdated:
      "Dieser Eintrag wurde bereits von einem anderen Benutzer aktualisiert. Aktualisieren Sie die Seite, um die neueste Version zu laden.",
    areaSalesManager: "Gebietsverkaufsleiter",
    arrived: "Angekommen",
    arrivingSoon: "Kommt bald an",
    ascending: "Aufsteigend",
    assign: "Zuweisen",
    assignCar: "Fahrzeug zuweisen",
    assignHp: "Neues Mautgerät zuweisen",
    assignNewDriver: "Neuen Fahrer zuweisen",
    assignNewPlate: "Neues Kennzeichen zuweisen",
    assigned: "Zugewiesen",
    assignedTo: "Zugewiesen an",
    assignSubsidiary: "Niederlassung zuweisen",
    awaitingForApproval: "Freigabe ausstehend",
    attachments: "Anhänge",
    attachmentsHelpDialog:
      "Es ist möglich, Bilder und/oder Dokumente anzuhängen; die hochgeladenen Dateien sind für Sie und die Systemadministratoren des Fleet Managers sichtbar.",
    avgYears: "Ø Jahre",
    back: "Zurück",
    birthdate: "Geburtsdatum",
    business: "Geschäftlich",
    by: "Von",
    cancel: "Abbrechen",
    cancelled: "Abgebrochen",
    cancelWorkflow: "Workflow abbrechen",
    cancelWorkflowRequest:
      "Der Workflow wird gestoppt. Alle ausstehenden Bild-Uploads werden abgebrochen. Möchten Sie fortfahren?",
    car: "Fahrzeug",
    carAssignment: "Fahrzeugzuweisung",
    carCategory: "Fahrzeugkategorie",
    carInCarPool: "Fahrzeug im Car Pool",
    carNotInUse: "Fahrzeug nicht in Verwendung",
    carPool: "Car Pool",
    carPoolCostCenters: "Car Pool Kostenstellen",
    carOrdersWithNearTermText: {
      prefix: "Es gibt Fahrzeugkäufe mit nahem Liefertermin, die noch nicht zugewiesen sind. Klicken Sie ",
      linkTitle: "hier",
      suffix: " um sie aufzulisten und zu bearbeiten.",
    },
    carReplacedRental: "Ersatzmietwagen",
    cartax: "Kfz-Steuer",
    carVin: "Fahrgestell-Nr.",
    categories: "Kategorien",
    category: "Kategorie",
    changeFlag: "Flagge ändern",
    checkWorkflow: "Workflow prüfen",
    clear: "Leeren",
    close: "Schließen",
    closed: "Geschlossen",
    awaitingDocuments: "Unterlagen ausstehend",
    closingDate: "Abschlussdatum",
    columnNotFilterable: "Diese Spalte kann nicht gefiltert werden",
    filterEquals: "Gleich",
    filterRange: "Von - Bis",
    filterMonth: "Monat",
    columnNotSortable: "Diese Spalte kann nicht sortiert werden",
    continue: "Weiter",
    contractDate: "Vertragsdatum",
    contractDetails: "Vertragsdetails",
    cost: "Kosten",
    costInputFormNet: "Kosten (Netto - Währung)",
    countDate: "Zähldatum",
    country: "Land",
    countryIsoCode: "Länder-ISO-Code",
    countryIsoCodeRequiredForAgreement:
      "Zum Erstellen einer Vereinbarung muss der Länder-ISO-Code der Niederlassung gesetzt sein.",
    costCenter: "Kostenstelle",
    costCenterHistory: "Kostenstellenhistorie",
    costObject: "Kostenobjekt",
    costImport: "Kostenimport",
    costImports: "Kostenimporte",
    costGross: "Kosten brutto",
    costNet: "Kosten netto",
    co2: "CO₂",
    create: "Erstellen",
    createdAt: "Erstellt am",
    createdBy: "Erstellt von",
    createDelegation: "Delegation erstellen",
    createDelegationWithDriverSelection: "Delegation mit Fahrerauswahl erstellen",
    createDelegationForExternal: "Delegation für externe Personen erstellen",
    createNew: "Neu erstellen",
    currency: "Währung",
    currentHighwayPass: "Aktuelles Mautgerät",
    currentDriver: "Aktueller Fahrer",
    currentlyInCarPool: "Dieses Fahrzeug befindet sich derzeit im Car Pool.",
    currentlyNotUsed: "Dieses Fahrzeug wird derzeit nicht verwendet.",
    currentlyAssignToDriver: "Dieses Fahrzeug ist derzeit einem anderen Fahrer zugewiesen.",
    currentPlateNumber: "Aktuelles Kennzeichen",
    currentSubsidiary: "Aktuelle Niederlassung",
    currentVersion: "Aktuelle Version",
    dashboard: "Dashboard",
    dataConflicts: (value: string) => `Datenkonflikt am Datum ${value}`,
    dataHasBeenSavedSuccessfully: "Daten wurden erfolgreich gespeichert.",
    dataHasBeenUpdatedSuccesfully: "Daten wurden erfolgreich aktualisiert.",
    date: "Datum",
    datePicker: {
      clearButton: "Datum löschen",
      nextMonth: "Nächster Monat",
      placeholder: "Datum wählen",
      previousMonth: "Vorheriger Monat",
      selectMonth: "Monat wählen",
      selectYear: "Jahr wählen",
    },
    decrease: (value: number) => `Verringern ${value}`,
    defaultCurrency: "Standardwährung",
    defaultLanguage: "Standardsprache",
    delegation: "Delegation",
    delete: "Löschen",
    deleteFilter: (filter: string) => `Filter für ${filter} löschen`,
    deleteFilters: "Filter löschen",
    department: "Abteilung",
    departments: "Abteilungen",
    descending: "Absteigend",
    description: "Beschreibung",
    diesel: "Diesel",
    discountGross: "Rabatt brutto",
    discountNet: "Rabatt netto",
    distance: "Entfernung",
    distances: "Entfernungen",
    distanceInMiles: "Entfernung in Meilen",
    divisionSalesManager: "Bereichsverkaufsleiter",
    dkvProductCodes: "DKV Produktcodes",
    documents: "Dokumente",
    done: "Erledigt",
    download: "Herunterladen",
    downloadFile: "Datei herunterladen",
    dragAndDropOrUploadAnImage: "Ziehen Sie ein Bild hierher oder klicken Sie zum Hochladen",
    driverAlreadyNotified:
      "Eine Benachrichtigungs-E-Mail wurde diesem Benutzer bereits gesendet. Möchten Sie sie erneut senden?",
    driverNotified: (on: string) => `Fahrer am ${on} benachrichtigt. Warten auf den Upload der Bilder.`,
    dragAndDropOrUploadAnFile: "Ziehen Sie eine Datei hierher oder klicken Sie zum Hochladen",
    driverName: "Fahrername",
    driverSince: "Fahrer seit",
    drivingLicenseExpirationDate: "Ablaufdatum des Führerscheins",
    drivingLicenseText:
      "Durch die Eingabe des Ablaufdatums Ihres Führerscheins in das System erhalten Sie automatisch etwa 20 Tage vor dem Ablauf eine Erinnerungsbenachrichtigung. Die eingegebenen Informationen werden weder von Fleet Management nachverfolgt noch verwaltet, sondern ausschließlich vom System verarbeitet.",
    dropIt: "Ablegen",
    dueDateHelpDialog: "Durch Eingabe eines Datums wird die Fälligkeit auf das ausgewählte Datum aktualisiert.",
    dueDateVisibleToUser:
      "Die Auswahl eines Benutzers macht das Fälligkeitsdatum sowohl für den Benutzer selbst als auch für die zugehörigen Manager sichtbar.",
    edit: "Bearbeiten",
    editedBy: "Bearbeitet von",
    editNotPermitted: "Bearbeiten ist nicht mehr erlaubt — seit der Erstellung ist mehr als ein Tag vergangen.",
    electric: "Elektrisch",
    email: "E-Mail",
    emailSendToDriver: "Eine E-Mail-Benachrichtigung wird an den Fahrer gesendet.",
    empty: "Leer",
    endDate: "Enddatum",
    engineCapacity: "Hubraum",
    english: "Englisch",
    englishUk: "Englisch - UK",
    englishUs: "Englisch - US",
    englishBrasil: "Englisch - Brasilien",
    enteredDate: "Eingabedatum",
    enterAPhoneNumber: "Bitte geben Sie eine Telefonnummer ein",
    enterValidNames: "Bitte geben Sie gültige Namen ein.",
    error: "Fehler",
    errorLoadingData: "Fehler beim Laden der Daten",
    expirationDate: "Ablaufdatum",
    expired: "Abgelaufen",
    expiringSoon: "Läuft bald ab",
    exportExcel: "Nach Excel exportieren",
    fieldCannotBeEmpty: "Dieses Feld darf nicht leer sein.",
    file: "Datei",
    fileCsv: "CSV-Datei",
    fileNotSupported: `Die Datei wird nicht unterstützt.`,
    fileTypeSupported: (extensions: string[]) => `Unterstützte Dateitypen: ${extensions.join(", ")}.`,
    fileToLarge: "Datei zu groß",
    fiscalPrice: "Fiskalpreis",
    fieldContainsInvalidValueInsertBiggerOne:
      "Das Feld enthält einen ungültigen Wert, geben Sie einen höheren Wert ein.",
    fieldContainsInvalidValueInsertSmallerOne:
      "Das Feld enthält einen ungültigen Wert, geben Sie einen niedrigeren Wert ein.",
    fieldInvalidValue: "Das Feld enthält einen ungültigen Wert.",
    fieldValueInvalidFormat: "Der Wert hat ein ungültiges Format.",
    firstName: "Vorname",
    flag: "Flagge",
    french: "Französisch",
    fringeBenefit: "Sachbezug",
    from: "Von",
    fuelCard: "Tankkarte",
    fuelType: "Kraftstoffart",
    gasoline: "Benzin",
    generalData: "Allgemeine Daten",
    generateAgreement: "Vereinbarung generieren",
    genericError: "Ein Fehler ist aufgetreten, bitte versuchen Sie es erneut.",
    german: "Deutsch",
    gpl: "LPG",
    hasImportErrors: "Importfehler vorhanden",
    hideToUser: "Für Benutzer ausblenden",
    hidden: "Versteckt",
    hiddenText:
      "Da Benutzer regelmäßig aus dem Rothoblaas-System importiert werden, könnten darunter Accounts sein, die im Verwaltungssystem nicht benötigt werden. Mit diesem Flag kann ein Benutzer von der Fahrerverwaltung ausgeblendet werden.",
    disableNotifications: "Benachrichtigungen deaktivieren",
    disableNotificationsText:
      "Wenn dieses Flag aktiviert ist, werden automatische Benachrichtigungs-E-Mails nicht versendet. Andere E-Mails, die durch Benutzeraktionen ausgelöst werden, werden weiterhin versendet.",
    hideArrivalNotification: "Benachrichtigung zum Ankunftsdatum ausblenden",
    history: "Historie",
    hp: "PS",
    hybrid: "Hybrid",
    imagesAccepted: "Bilder akzeptiert",
    imagesDeleted: "Bilder gelöscht",
    imagesSupplied: "Bilder bereitgestellt",
    imperial: "Imperial (Meilen)",
    importInCosts: "In Kosten importieren",
    import: "Importieren",
    importCsv: "CSV importieren",
    importErrors: "Importfehler",
    importErrorsText:
      "Die Kostenstellen werden automatisch aus bValue (Provisius) importiert.<br/>Die untenstehenden Zeilen zeigen Datensätze, die nicht importiert werden konnten.<br/>Bitte prüfen Sie die Von/Bis-Daten mit HR und korrigieren Sie diese unten.",
    importDkv: "DKV importieren",
    importHighwayToll: "Mautgebühren importieren",
    importedInCosts: "In Kosten importiert",
    increase: (value: number) => `Erhöhen ${value}`,
    insert: "Einfügen",
    insurance: "Versicherung",
    invalidDate: "Ungültiges Datum",
    invalidLenght: "Ungültige Länge",
    invalidNumber: "Ungültige Nummer",
    isInPool: "Ist im Car Pool",
    isRothoblaasUser: "Ist Rothoblaas-Benutzer",
    isRothoblaasUserTexts: [
      "Dieses Flag dient dazu, Rothoblaas-Benutzer von externen Benutzern zu unterscheiden. Wenn es aktiviert ist, überprüft das System, ob die E-Mail-Adresse gültig ist. In diesem Fall werden Vor- und Nachname aus der Rothoblaas-Datenbank übernommen. Außerdem ist es nicht möglich, einen Benutzer mit einer Rothoblaas-E-Mail-Adresse zu speichern, wenn keine Übereinstimmung mit aktiven Benutzern besteht.",
      "Wenn das Flag deaktiviert ist, bleibt die E-Mail-Adresse optional und es werden keine Überprüfungen der Rothoblaas-Benutzer durchgeführt. In diesem Fall sind jedoch Vor- und Nachname verpflichtend.",
    ],
    isSettled: "Ist abgeschlossen",
    italian: "Italienisch",
    portuguese: "Portugiesisch",
    withErrors: "Mit Fehlern",
    withoutErrors: "Ohne Fehler",
    itemsNotExported: "Nicht exportiert",
    itemsForced: "Erzwungen",
    forcedBy: "Erzwungen von",
    keep: "Beibehalten",
    kilometers: "Kilometer",
    language: "Sprache",
    languageCode: "Sprachcode",
    lastDriver: "Letzter Fahrer",
    lastEdit: "Letzte Bearbeitung durch",
    lastHighwayPass: "Letztes Mautgerät",
    lastMileage: "Letzter Kilometerstand",
    lastMileageRec: "Letzter Kilometerstand-Datensatz",
    lastName: "Nachname",
    lastPlate: "Letztes Kennzeichen",
    lastRecord: "Letzter Eintrag",
    lastUpdate: "Letztes Update",
    lastUpdatedBy: "Zuletzt aktualisiert von",
    latvian: "Lettisch",
    leasing: "Leasing",
    lessThanDueDateRecord: "ist kleiner als das Datum eines der ausgewählten Einträge",
    licensePlateValidityEnd: "Ablauf Kennzeichengültigkeit",
    licensePlateValidityStart: "Beginn Kennzeichengültigkeit",
    lithuanian: "Litauisch",
    loading: "Laden",
    logout: "Abmelden",
    lkw: "Lkw",
    lkwAccountingNumber: "Lkw-Kontonummer",
    lkwAccountingNumberShort: "Lkw Konto-Nr.",
    manageCategories: "Kategorien verwalten",
    manageClaimTypes: "Schadensarten verwalten",
    manageCostTypeCategories: "Kostenartkategorien verwalten",
    manageCostTypes: "Kostenarten verwalten",
    vatCodes: "MwSt-Codes",
    manageDefaultTasks: "Standardaufgaben verwalten",
    manageDueDateTypes: "Fälligkeitstypen verwalten",
    manageMaintenanceTypes: "Wartungstypen verwalten",
    manager: "Manager",
    managerEmail: "Manager-E-Mail",
    managerName: "Managername",
    metric: "Metrisch (km)",
    mileage: "Kilometerstand",
    mileages: "Kilometerstände",
    miles: "Meilen",
    missingContractDate: "Vertragsdatum fehlt",
    missingCountry: "Land fehlt",
    missingExpirationDate: "Ablaufdatum fehlt",
    missingLeasingCompany: "Leasinggesellschaft fehlt",
    missingRenewalDate:
      "Achtung! Es wurde kein Erneuerungsdatum eingegeben; die Fälligkeit wird nicht aktualisiert und lediglich geschlossen.",
    modifiedAt: "Bearbeitet am",
    modifiedBy: "Bearbeitet von",
    model: "Modell",
    monthYearPicker: {
      clearButton: "Monat löschen",
      nextYear: "Nächstes Jahr",
      placeholder: "Monat wählen",
      previousYear: "Vorheriges Jahr",
      selectYear: "Jahr wählen",
    },
    myArea: "Mein Gebiet",
    myCar: "Mein Fahrzeug",
    myCars: "Meine Fahrzeuge",
    myProfile: "Mein Profil",
    myTeamCar: "Teamfahrzeug",
    myTeamCars: "Teamfahrzeuge",
    name: "Name",
    needsAttention: "Handlungsbedarf",
    ordersWithNearArrivalDate: "Fahrzeugkäufe mit nahem Ankunftsdatum",
    newPlateNumber: "Neues Kennzeichen",
    newOrder: "Neuanschaffung",
    nextNRows: (n: number) => `Nächste ${n} Zeilen`,
    newLeasing: "Neues Leasing",
    no: "Nein",
    noDriver: "Kein Fahrer",
    noFileSelected: "Keine Datei ausgewählt",
    noHighwayPasses: "Keine Mautgeräte",
    noDueDate: "Keine Fälligkeiten",
    noDueDateSelected: "Keine Fälligkeit ausgewählt",
    noItemsFound: "Keine Elemente gefunden.",
    noLeasing: "Kein Leasing",
    noMilegaes: "Keine Kilometerstände",
    notes: "Notizen",
    noTasksToDo: "Keine Aufgaben zu erledigen",
    notFound: "Nicht gefunden",
    notifications: "Benachrichtigungen",
    notifyDriver: "Fahrer benachrichtigen",
    notInUse: "Nicht in Verwendung",
    notPlannedPurchase: "Nicht geplanter Kauf",
    notRequired: "Nicht erforderlich",
    notSettled: "Nicht abgeschlossen",
    number: "Nummer",
    count: "Anzahl",
    open: "Offen",
    ok: "Ok",
    on: "am",
    openingDate: "Eröffnungsdatum",
    operation: "Vorgang",
    optional: "Optional",
    optionals: "Optionale Elemente",
    order: "Bestellung",
    orderImperativ: "Bestellen",
    orderCar: "Auto bestellen",
    orderDate: "Bestelldatum",
    ordered: "Bestellt",
    overview: "Übersicht",
    overwrite: "Überschreiben",
    otherAssignmentType: "Car Pool / Nicht in Verwendung",
    others: "Sonstiges",
    parking: "Parkplatz",
    permissions: "Berechtigungen",
    phoneNumber: "Telefonnummer",
    phoneNumberIsInvalid: "Telefonnummer ist für dieses Land ungültig",
    picture: "Bild",
    plannedArrivalDate: "Geplantes Ankunftsdatum",
    plugInHybrid: "Plug-in-Hybrid",
    pkw: "Pkw",
    pkwAccountingNumber: "Pkw-Kontonummer",
    pkwAccountingNumberShort: "Pkw Konto-Nr.",
    pkwSuperior: "Pkw Superior",
    pkwSuperiorAccountingNumber: "Pkw-Superior-Kontonummer",
    pkwSuperiorAccountingNumberShort: "Pkw Sup. Konto-Nr.",
    pkwUp: "Pkw Up",
    pkwUpAccountingNumber: "Pkw-Up-Kontonummer",
    pkwUpAccountingNumberShort: "Pkw Up Konto-Nr.",
    positionCode: "Positionscode",
    positionName: "Positionsname",
    preferredLanguage: "Bevorzugte Sprache",
    preview: "Vorschau",
    previousEndDateLowerStartDate:
      "Bitte geben Sie für den vorherigen Eintrag ein Enddatum ein, das nach dem Startdatum liegt.",
    previousEndDateUndefined: "Bitte setzen Sie ein Enddatum für den vorherigen Eintrag.",
    previousNRows: (n: number) => `Vorherige ${n} Zeilen`,
    purchasePriceInputForm: "Kaufpreis (ohne MwSt - mit MwSt - Währung)",
    priceWithVat: "Preis mit MwSt",
    priceWithoutVat: "Preis ohne MwSt",
    pricePerUnit: "Preis pro Einheit",
    private: "Privat",
    privateCar: "Privatfahrzeug",
    productCategory: "Produktkategorie",
    productCode: "Produktcode",
    productType: "Produkttyp",
    purchase: "Kauf",
    purchaseDate: "Kaufdatum",
    purchaseType: "Kauf / Leasing",
    quantity: "Menge",
    read: "Lesen",
    reAssignment: "Neuzuweisung",
    recipient: "Empfänger",
    responsible: "Verantwortlich",
    responsibleCode: "Verantwortlicher Code",
    resourceNotFound: "Die gesuchte Ressource wurde nicht gefunden.",
    returned: "An Leasing zurückgegeben",
    regionSalesManager: "Regionaler Verkaufsleiter",
    registrationDate: "Zulassungsdatum",
    rejectImages: "Bilder ablehnen",
    rejected: "Abgelehnt",
    rejectImagesForm:
      "Die hochgeladenen Bilder werden abgelehnt und gelöscht. Der Fahrer wird per E-Mail darüber informiert, dass neue Bilder erforderlich sind. Sie können eine Nachricht angeben, die in die E-Mail aufgenommen wird:",
    reloadData: "Daten neu laden",
    remove: "Entfernen",
    rename: "Umbenennen",
    renameDocument: "Dokument umbenennen",
    reopen: "Wieder öffnen",
    reopenWorkflow: "Workflow wieder öffnen",
    renewalDate: "Verlängerungsdatum",
    roles: "Rollen",
    rolesAndPermissions: "Rollen und Berechtigungen",
    salesRole: "Verkaufsrolle",
    save: "Speichern",
    saving: "Speichern...",
    search: "Suchen",
    select: "Auswählen",
    selectAll: "Alle auswählen",
    selectACountry: "Bitte wählen Sie ein Land aus",
    selectDate: "Datum auswählen",
    selectFile: "Datei auswählen",
    selectFlag: "Flagge auswählen",
    semiramisOrganizationCode: "Semiramis Organisationscode",
    sendDelegation: "Delegation senden",
    send: "Senden",
    sendEmail: "E-Mail senden",
    setAsDone: "Als erledigt markieren",
    inProgress: "In Bearbeitung",
    setAsNotRequired: "Als nicht erforderlich markieren",
    setAsTodo: "Als zu erledigen markieren",
    setIsoCode: "ISO-Code festlegen",
    setNotInUse: "Als nicht in Verwendung festlegen",
    setSubsidiaryIsoCode: "ISO-Code der Niederlassung festlegen",
    settings: "Einstellungen",
    settled: "Abgeschlossen",
    setToday: "Heute setzen",
    showArrivalNotification: "Benachrichtigung über Ankunftsdatum anzeigen",
    showToUser: "Für Benutzer anzeigen",
    sold: "Verkauft",
    someCostCenterPeriodsOverlap: "Einige Kostenstellenzeiträume überschneiden sich.",
    sortAscending: "Aufsteigend sortieren",
    sortDescending: "Absteigend sortieren",
    spanish: "Spanisch",
    startDate: "Startdatum",
    startDateBeforeEndDatePrevRecord:
      "Bitte geben Sie ein Startdatum ein, das nach dem Enddatum des vorherigen Eintrags liegt.",
    startDateEqualEndDate: "Das Startdatum darf nicht dem Enddatum entsprechen.",
    startDateEqualPrevEndDate: "Startdatum entspricht dem Enddatum des vorherigen Eintrags",
    startDistance: "Startentfernung",
    state: "Status",
    serviceFeeNet: "Servicegebühr netto",
    serviceFeeGross: "Servicegebühr brutto",
    since: "Seit",
    soldDate: "Verkaufsdatum",
    subsidiaryRequiredForAgreement:
      "Für die Erstellung der Vereinbarung muss das Fahrzeug einer Niederlassung zugeordnet sein.",
    subsidiaryRequiredForDelegation:
      "Für die Erstellung der Delegation muss das Fahrzeug einer Niederlassung zugeordnet sein.",
    subsidiaryHasCustomTasks: "Niederlassung hat eigene Aufgaben",
    subsidiaryUsesDefaultTasks: "Niederlassung verwendet Standardaufgaben",
    tasks: "Aufgaben",
    taskCancelled: "Aufgabe abgebrochen - keine Aktion erforderlich",
    teamCar: "Teamfahrzeug",
    teamMembers: "Teammitglieder",
    technicalSale: "Technischer Verkäufer",
    telepass: "Mautgerät",
    testEnvironment: "Testumgebung",
    time: "Zeit",
    to: "Bis",
    toApprove: "Zu genehmigen",
    toAssign: "Zuzuweisen",
    todo: "Zu erledigen",
    tolls: "Mautgebühren",
    toOrder: "Zu bestellen",
    total: "Gesamt",
    type: "Typ",
    typeOfUse: "Verwendungsart",
    unassigned: "Nicht zugewiesen",
    unit: "Einheit",
    until: "Bis",
    userCostCenters: "Benutzerkostenstellen",
    userCostCentersHasImportErrorsText: {
      prefix: "Der Import der Benutzerkostenstellen aus bValue (Provisius) enthält Fehler. Klicken Sie ",
      linkTitle: "hier",
      suffix: " um sie aufzulisten und zu korrigieren.",
    },
    usersWithImportErrors: "Benutzer mit Importfehlern",
    userWithoutCostCenter: "Benutzer ohne Kostenstelle",
    update: "Aktualisieren",
    uploadedAt: "Hochgeladen am",
    uploadedBy: "Hochgeladen von",
    uploadImages: "Bilder hochladen",
    uploadImagesRequired: "Erforderliche Bilder hochladen",
    vat: "MwSt",
    vatCode: "MwSt-Code",
    vatDeductionCode: "MwSt-Abzugs-Code",
    vatServiceCode: "MwSt-Service-Code",
    vehicleIdentificationNumber: "Fahrgestellnummer",
    version: "Version",
    versions: "Versionen",
    versionNumber: "Versionsnummer",
    viewImages: "Bilder anzeigen",
    waitingUser: "Warten auf Benutzer",
    waitingForImageApproval: "Warten auf Bildfreigabe",
    view: "Anzeigen",
    visibleToUser: "Für den Benutzer sichtbar",
    visibleToUserInfo:
      "In diesem Feld können Sie einen Benutzer auswählen, mit dem das Fälligkeitsdatum geteilt werden soll. Standardmäßig steht der aktuell dem Fahrzeug zugewiesene Benutzer zur Auswahl. Wenn das Fälligkeitsdatum zuvor mit einem anderen Benutzer geteilt wurde, können sowohl der aktuell zugewiesene Benutzer als auch der zuvor ausgewählte Benutzer ausgewählt werden.",
    warningCloseEntities: (from: string, to: string) =>
      `Der definierte Zeitraum (${from}-${to}) steht im Konflikt mit anderen Einträgen. Durch die Bestätigung wird ${to} als Enddatum für die oben aufgeführten Einträge festgelegt.`,
    warningReOpenEntities: (from: string, to: string) =>
      `Der ausgewählte Zeitraum (${from}-${to}) steht im Konflikt mit anderen Datensätzen. Wählen Sie die Kontrollkästchen der Datensätze aus, die in die neue Tochtergesellschaft übernommen werden sollen. Durch die Bestätigung wird ${to} als Enddatum für die ausgewählten Datensätze und als Startdatum für die neuen festgelegt.`,
    when: "Wann",
    workflowCancelled: "Workflow abgebrochen. Sie können ihn mit dem untenstehenden Button wieder öffnen.",
    workflowImages: "Workflow-Bilder",
    year: "Jahr",
    yes: "Ja",

    endDateCantMatchStartDate: "Das Enddatum darf nicht dem Startdatum entsprechen",
    endDateBeforeStartDate: "Das Enddatum darf nicht vor dem Startdatum liegen",
    endDateAfterCurrentAssignmentStartDate:
      "Das Enddatum darf nicht nach dem Startdatum der aktuellen Zuweisung liegen",
    startDateFallsInCurrentAssignmentPeriod: "Das Startdatum liegt im Zeitraum der aktuellen Zuweisung",
    endDateFallsInCurrentAssignmentPeriod: "Das Enddatum liegt im Zeitraum der aktuellen Zuweisung",
    endDateAfterStartDateNextRec:
      "Bitte geben Sie ein Enddatum ein, das vor dem Startdatum des nächsten Eintrags liegt.",
    endDateEqualNextStartDate: "Enddatum entspricht dem Startdatum des nächsten Eintrags",
  },
};
export default de;
